import { useContext, useEffect, useState } from 'react';
import DatePicker from 'react-datepicker';
import { UsersContextDetail } from '../../../userContextDetail';
import moment from 'moment';
import { toast } from 'react-toastify';

interface RescheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialDate?: Date;
  booking: any;
  onConfirm: (date: Date) => void;
}

const RescheduleModal = ({
  isOpen,
  onClose,
  initialDate,
  booking,
  onConfirm,
}: RescheduleModalProps) => {
  const context = useContext(UsersContextDetail);
  const updateBooking = context?.updateBooking;

  const [selectedDate, setSelectedDate] = useState(initialDate || new Date());
  const [selectedStartTime, setSelectedStartTime] = useState(new Date());
  const [selectedEndTime, setSelectedEndTime] = useState(
    moment().add(3, 'hours').toDate()
  );

  const isPastOrToday =
    selectedDate < new Date(new Date().setHours(0, 0, 0, 0));

  useEffect(() => {
    if (isOpen && booking) {
      const startDate = booking?.startDate
        ? new Date(booking.startDate)
        : initialDate || new Date();

      const startTime = booking?.startTime
        ? moment(booking.startTime, 'HH:mm A').toDate()
        : new Date();

      const endTime = booking?.endTime
        ? moment(booking.endTime, 'HH:mm A').toDate()
        : moment(startTime).add(3, 'hours').toDate();

      setSelectedDate(startDate);
      setSelectedStartTime(startTime);
      setSelectedEndTime(endTime);
    }
  }, [isOpen, booking, initialDate]);

  const handleDateChange = (date: any) => {
    setSelectedDate(date);
  };

  const handleStartTimeChange = (time: any) => {
    setSelectedStartTime(time);
    setSelectedEndTime(moment(time).add(3, 'hours').toDate());
  };

  const handleEndTimeChange = (time: any) => {
    setSelectedEndTime(time);
  };

  const handleConfirm = () => {
    if (isPastOrToday) {
      toast.warning('Please select a future date to proceed.');
      return;
    }

    const duration = moment(selectedEndTime).diff(
      moment(selectedStartTime),
      'hours'
    );
    if (duration < 3) {
      toast.warning('End time must be at least 3 hours after start time.');
      return;
    }

    if (updateBooking) {
      const startTime = moment(selectedStartTime).format('HH:mm A');
      const endTime = moment(selectedEndTime).format('HH:mm A');
      updateBooking(
        booking.id,
        moment(selectedDate).format('YYYY-MM-DD'),
        booking.meals,
        booking.additionalServices,
        booking.status,
        startTime,
        endTime
      );
    }

    toast.success('Booking updated successfully');
    onConfirm(selectedDate);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="rescheduleContainer" onClick={onClose}>
      <div className="reschedulewrapper" onClick={(e) => e.stopPropagation()}>
        <h4 className="text-center mb-4">Reschedule Booking</h4>

        <div className="mb-3">
          <label className="fst-normal mb-1 d-block">Select New Date</label>
          <DatePicker
            selected={selectedDate}
            onChange={handleDateChange}
            dateFormat="dd/MM/yyyy"
            minDate={moment().add(1, 'days').toDate()}
            className="form-control border"
            placeholderText="Choose a date"
          />
        </div>

        <div className="mb-3">
          <label className="fst-normal mb-1 d-block">Start Time</label>
          <DatePicker
            selected={selectedStartTime}
            onChange={handleStartTimeChange}
            showTimeSelect
            showTimeSelectOnly
            timeIntervals={30}
            timeCaption="Start Time"
            dateFormat="h:mm aa"
            className="form-control border"
            placeholderText="Choose start time"
            disabled={isPastOrToday}
          />
        </div>

        <div className="mb-3">
          <label className="fst-normal mb-1 d-block">
            End Time (at least 3 hrs after start)
          </label>
          <DatePicker
            selected={selectedEndTime}
            onChange={handleEndTimeChange}
            showTimeSelect
            showTimeSelectOnly
            timeIntervals={30}
            timeCaption="End Time"
            dateFormat="h:mm aa"
            className="form-control border"
            placeholderText="Choose end time"
            disabled={isPastOrToday}
            minTime={moment(selectedStartTime).add(3, 'hours').toDate()}
            maxTime={new Date(0, 0, 0, 23, 59)}
          />
        </div>

        <div className="d-flex justify-content-end gap-2">
          <button onClick={onClose} className="reschedulCancel">
            Cancel
          </button>
          <button onClick={handleConfirm} className="reschedulSave">
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
};

export default RescheduleModal;
